import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BcmService } from '@modules/bcm/bcm.service';
import { BcmBaseFacade } from '../bcm-base.facade';
import { ConfirmDialogService } from '@sharedComponents/dialogs/confirm-dialog/confirm-dialog.service';
import { Company, ICompany } from '@shared/models/company';
import { BcmUserPermission } from '@modules/bcm/bcm-user-permission';
import { BcmExportExcelApiService } from '@bcmApiServices/bcm-export-excel.api-service';
import { AppNotificationService } from '@core/services/app-notification.service';
import { companiesTableData } from '@modules/bcm/@core/state-management/companies/companies-table-data';
import { BcmNavigationService } from '@modules/bcm/bcm-navigation.service';
import { ThemePalette } from '@angular/material/core';
import { BcmUserSettingsFacade } from '@bcmServices/settings/bcm-user-settings-facade';
import {
    filteredListExportHeaderActionBase,
    predefinedReportsExcelHeaderActionBase,
} from '@modules/bcm/@core/state-management/filtered-list-export-header.util';
import {
    GetTenantRelationDataDialogComponent
} from '@sharedComponents/dialogs/get-tenant-relation-data/get-tenant-relation-data-dialog.component';
import { EMPTY, forkJoin, of, switchMap } from 'rxjs';
import { TenantRelationAssignment } from '@shared/models/tenant-relation-assignment';
import { tap } from 'rxjs/operators';
import { InvoicePosition } from '@shared/models/invoice-position';
import { BcmInvoicePositionsService } from '@bcmServices/invoice-positions.service';
import { MatDialog } from '@angular/material/dialog';
import { CompanyApiService } from '@modules/bcm/@shared/services';
import { BcmBaseBulkChanges } from '@modules/bcm/@core/state-management/bcm-base-bulk-changes';
import {
    GetCountryDialogComponent
} from '@modules/bcm/@shared/components/dialogs/get-country-dialog/get-country-dialog.component';
import { Country } from '@shared/models/country';
import { TolgeeService } from '@core/translation/tolgee.service';

@Injectable({providedIn: 'root'})
export class CompaniesFacade extends BcmBaseFacade<Company, ICompany> {

    readonly resourcePath = 'companies';

    protected override get listResourcePath(): string {
        return 'companies/index';
    }

    get resourceNameSingular(): string {
        return TolgeeService.translate('company.singular');
    }

    get resourceNamePlural(): string {
        return TolgeeService.translate('company.plural');
    }

    readonly resourceIconPlural = 'location_city';

    readonly resourceIconSingular = 'location_city';

    readonly deletePermission = BcmUserPermission.COMPANIES_DELETE;

    readonly readPermission = BcmUserPermission.COMPANIES_READ;

    readonly writePermission = BcmUserPermission.COMPANIES_WRITE;

    readonly hideGenericExcelButton = true;

    readonly headerActions = [
        {
            titleKey: 'table.exportCurrentView',
            icon: 'table_view',
            onClick: () => this.exportTableAsExcel(),
            ...filteredListExportHeaderActionBase(),
        },
        {
            titleKey: 'table.excelExport',
            onClick: () => this._bcmExportExcelApiService.exportCompanies(),
            ...predefinedReportsExcelHeaderActionBase(),
        },
        {
            titleKey: 'table.excelExportTenantRelations',
            onClick: () => this._bcmExportExcelApiService.exportCompanyTenantRelations(),
            ...predefinedReportsExcelHeaderActionBase(),
        }
    ];

    readonly rowActions = [
        {
            titleKey: 'table.rowActionView',
            icon: 'assignment',
            iconColor: 'accent' as ThemePalette,
            onClick: (company: Company) => this._bcmNavigationService.navigate('companies/' + company.id),
        },
        {
            titleKey: 'table.rowActionDelete',
            icon: 'delete',
            iconColor: 'warn' as ThemePalette,
            onClick: (company: Company) => super.remove(company, company.name).subscribe(),
        }
    ];

    readonly bulkChanges = new BcmBaseBulkChanges<Company>()
        .setButtons(
            {
                titleKey: 'table.bulkTenantRelation',
                icon: 'add',
                iconColor: 'accent' as ThemePalette,
                resetSelectionAfterSuccess: true,
                onClick: (companies: Company[]) => this.addTenantRelation(...companies),
            },
            {
                titleKey: 'table.bulkInvoicePositionCompany',
                icon: 'add',
                iconColor: 'accent' as ThemePalette,
                resetSelectionAfterSuccess: true,
                onClick: (companies: Company[]) => this.addInvoicePosition(...companies),
            },
            {
                titleKey: 'table.bulkCountry',
                icon: 'edit',
                iconColor: 'accent' as ThemePalette,
                resetSelectionAfterSuccess: true,
                onClick: (companies: Company[]) => this.updateCountry(...companies),
            },
            {
                titleKey: 'common.delete',
                icon: 'delete',
                iconColor: 'warn' as ThemePalette,
                resetSelectionAfterSuccess: true,
                onClick: (companies: Company[]) => super.removeMultiple(companies),
            },
        );

    constructor(appNotificationService: AppNotificationService,
                confirmDialogService: ConfirmDialogService,
                bcmUserSettingsFacade: BcmUserSettingsFacade,
                httpClient: HttpClient,
                bcmService: BcmService,
                bcmNavigationService: BcmNavigationService,
                private _bcmExportExcelApiService: BcmExportExcelApiService,
                private _bcmNavigationService: BcmNavigationService,
                private _bcmInvoicePositionsService: BcmInvoicePositionsService,
                private _companiesApiService: CompanyApiService,
                private _matDialog: MatDialog,
    ) {
        super(
            Company,
            companiesTableData,
            bcmNavigationService,
            appNotificationService,
            confirmDialogService,
            bcmUserSettingsFacade,
            httpClient,
            bcmService
        );
    }

    addTenantRelation(...companies: Company[]) {
        const company = companies.length === 1 ? companies[0] : undefined;
        const dialogRef = this._matDialog.open(GetTenantRelationDataDialogComponent, {data: {company}});

        return dialogRef
            .afterClosed()
            .pipe(
                switchMap((tenantRelationAssignment: TenantRelationAssignment) => {

                    if (!tenantRelationAssignment) {
                        return of(undefined);
                    }

                    return this._companiesApiService
                        .multipleNewTenantRelationAssignment(tenantRelationAssignment, ...companies)
                        .pipe(tap((tenantRelationAssignments) => {
                            companies.forEach(c => c.addTenantRelationAssignment(
                                tenantRelationAssignments.find(assignment => assignment.company?.id === c.id)
                            ));
                            this.appNotificationService.showSuccess(TolgeeService.translate('bcmTenantRelations.notifications.relationsCreated'));
                        }));
                })
            );
    }

    addInvoicePosition(...companies: Company[]) {
        return this._bcmInvoicePositionsService
            .addPositionsForMultipleCompanies(companies)
            .pipe(tap((positions: InvoicePosition[]) => {
                if (positions.length > 0) {
                    this.appNotificationService.showSuccess(TolgeeService.translate('person.invoices.positions.notifications.added'));
                }
            }));
    }

    updateCountry(...companies: Company[]) {
        const dialogRef = this._matDialog.open(GetCountryDialogComponent);

        return dialogRef
            .afterClosed()
            .pipe(
                switchMap((country: Country) => {
                    if (country) {
                        companies.forEach(company => company.country = country);
                        return forkJoin(
                            companies.map(company => super.update(company, {country} as Partial<Company>))
                        );
                    }
                    return EMPTY;
                })
            );
    }
}
